import React from 'react';
import { Divider, Button, Popover } from 'antd';
import {
    SettingOutlined,
    UnorderedListOutlined,
    NumberOutlined,
    FilterOutlined,
    SyncOutlined,
    ArrowDownOutlined,
    CheckCircleOutlined,
    RetweetOutlined,
    GroupOutlined,
    RiseOutlined,
    ScissorOutlined,
    TrophyOutlined,
    ColumnWidthOutlined,
    QuestionCircleOutlined,
} from '@ant-design/icons';
import {
    countNodeFilters,
    countNodeParameters,
    formatParameterDescription,
    getNodeTypeOption,
} from '../../../constants/nodeTypeParameters';
import '../styles/card.css';
import '../styles/workflow.css';

const NODE_TYPE_ICONS: Record<number, React.ReactNode> = {
    1: <UnorderedListOutlined />,
    2: <NumberOutlined />,
    3: <FilterOutlined />,
    4: <SyncOutlined />,
    5: <ArrowDownOutlined />,
    6: <CheckCircleOutlined />,
    7: <RetweetOutlined />,
    8: <GroupOutlined />,
    9: <RiseOutlined />,
    10: <ScissorOutlined />,
    11: <TrophyOutlined />,
    12: <ColumnWidthOutlined />,
};

const NodeIcon = ({ type }: { type: number }) => (
    <span className="workflow-node-icon">
        {NODE_TYPE_ICONS[type] ?? <QuestionCircleOutlined />}
    </span>
);

const getNodeFiltersList = (node) => {
    if (Array.isArray(node.filters)) {
        return node.filters;
    }

    if (Array.isArray(node.parameters?.filters)) {
        return node.parameters.filters;
    }

    return [];
};

const resolveSourceLabel = (fromId, nodes = []) => {
    const sourceNode = nodes.find((item) => item.id === fromId);
    return sourceNode ? `${sourceNode.id} - ${sourceNode.title}` : `#${fromId}`;
};

const NodeDetailsContent = ({ node, nodes = [] }) => {
    const nodeType = node.type ?? 1;
    const typeOption = getNodeTypeOption(nodeType);
    const filtersList = getNodeFiltersList(node);
    const fromIds = Array.isArray(node.from) ? node.from : [];

    return (
        <div className="workflow-node-details">
            <div className="workflow-node-details-row">
                <span className="workflow-node-details-label">Tipo</span>
                <span>{typeOption.label}</span>
            </div>

            {typeOption.from && (
                <div className="workflow-node-details-row">
                    <span className="workflow-node-details-label">Fontes</span>
                    <span>
                        {fromIds.length
                            ? fromIds.map((fromId) => resolveSourceLabel(fromId, nodes)).join(', ')
                            : '—'}
                    </span>
                </div>
            )}

            {typeOption.params && (
                <div className="workflow-node-details-row">
                    <span className="workflow-node-details-label">Parâmetros</span>
                    <span>{formatParameterDescription(nodeType, node.parameters ?? {})}</span>
                </div>
            )}

            {typeOption.filter && (
                <div className="workflow-node-details-row">
                    <span className="workflow-node-details-label">Filtros</span>
                    <span>
                        {filtersList.length
                            ? filtersList.map((filter, index) => (
                                <span key={`${filter.name}-${index}`} className="workflow-node-details-filter">
                                    {filter.name || '(sem nome)'}
                                </span>
                            ))
                            : '—'}
                    </span>
                </div>
            )}
        </div>
    );
};

const CardNode = ({ node, nodes = [], running = false, onSettingsClick }) => {
    const nodeType = node.type ?? 1;
    const typeOption = getNodeTypeOption(nodeType);
    const inputSources = Array.isArray(node.from) && node.from.length > 0 ? node.from : [null];
    const parametersCount = countNodeParameters(nodeType, node.parameters ?? {});
    const filtersCount = countNodeFilters(node);
    const typeSummary = typeOption.label;
    const typeSummary2 = `P: ${parametersCount} | F: ${filtersCount}`;

    return (
        <div className="workflow-node" data-node-id={node.id}>
            <Button
                type="text"
                size="small"
                icon={<SettingOutlined />}
                aria-label="Configurações da etapa"
                disabled={running}
                className="workflow-node-settings"
                onClick={(e) => {
                    e.stopPropagation();
                    onSettingsClick?.(node);
                }}
            />

            <Popover
                trigger="hover"
                mouseEnterDelay={0.25}
                placement="right"
                overlayClassName="workflow-node-popover"
                title={`${node.id} - ${node.title}`}
                content={<NodeDetailsContent node={node} nodes={nodes} />}
            >
                <div className="workflow-node-popover-target">
                    <div className="workflow-node-inputs">
                        {inputSources.map((fromId, index) => (
                            <div key={`${node.id}-in-${index}`} className="workflow-port-row">
                                <span
                                    className="workflow-port workflow-port-input"
                                    data-port-in={`${node.id}-${index}`}
                                    data-from-id={fromId ?? ''}
                                />
                            </div>
                        ))}
                    </div>

                    <div className="workflow-node-body">
                        <h3 className="workflow-node-title">
                            {node.id} - <b>{node.title}</b>
                        </h3>
                        <Divider style={{ margin: '4px 0' }} />
                        <div className="workflow-node-icon-wrap">
                            <NodeIcon type={nodeType} />
                        </div>
                        <span className="workflow-node-subtitle">
                            {typeSummary}
                        </span>
                        <span className="workflow-node-subtitle">
                            {typeSummary2}
                        </span>
                    </div>

                    <div className="workflow-node-output">
                        <span
                            className="workflow-port workflow-port-output"
                            data-port-out={node.id}
                        />
                    </div>
                </div>
            </Popover>
        </div>
    );
};

export default CardNode;
